/**
 * Раздел «Администрирование»: вкладки подразделов.
 * Макет data/Задачник.dc.html, adminSecondaryNav: Справочники, Пользователи,
 * Роли и права.
 *
 * Прежде навигации не было вовсе: шапка вела на пользователей, а роли
 * и справочники открывались только по прямому адресу.
 *
 * На телефоне раздел только для просмотра — см. adminReadOnly.ts.
 */

import { Permission } from "@taskflow/contracts";
import { ShieldOff } from "lucide-react";
import { Link, Navigate, Outlet, useLocation } from "react-router-dom";

import { usePermission } from "@taskflow/client-core";
import { cn, EmptyState } from "@/shared/ui";

import { useAdminReadOnly } from "./adminReadOnly";

interface Section {
  to: string;
  /** Вкладка активна на всех адресах с этим началом: /admin/users/:id — тоже «Пользователи». */
  match: string;
  label: string;
}

/** Вкладки, доступные пользователю, в порядке макета. */
function useSections(): Section[] {
  const dictionaries = usePermission(Permission.ADMIN_DICTS_MANAGE);
  const users = usePermission(Permission.ADMIN_USERS_VIEW);
  const roles = usePermission(Permission.ADMIN_ROLES_MANAGE);
  return [
    dictionaries && {
      to: "/admin/dictionaries/projects",
      match: "/admin/dictionaries",
      label: "Справочники",
    },
    users && { to: "/admin/users", match: "/admin/users", label: "Пользователи" },
    roles && { to: "/admin/roles", match: "/admin/roles", label: "Роли и права" },
  ].filter((section): section is Section => section !== false);
}

export function AdminLayout() {
  const sections = useSections();
  const { pathname } = useLocation();
  const readOnly = useAdminReadOnly();

  return (
    <div className="flex flex-col">
      <nav aria-label="Разделы администрирования" className="border-b border-line bg-surface">
        <div className="mx-auto flex w-full max-w-6xl gap-1 overflow-x-auto px-5">
          {sections.map((section) => {
            const active = pathname.startsWith(section.match);
            return (
              <Link
                key={section.to}
                to={section.to}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "border-b-2 px-3 py-2.5 text-[13px] font-semibold whitespace-nowrap transition-colors",
                  active ? "border-accent text-fg" : "border-transparent text-muted hover:text-fg",
                )}
              >
                {section.label}
              </Link>
            );
          })}
        </div>
      </nav>
      {readOnly && (
        <p className="border-b border-line bg-sunken px-4 py-2 text-[12px] text-muted">
          Здесь только просмотр. Изменения доступны с компьютера или планшета.
        </p>
      )}
      <Outlet />
    </div>
  );
}

/** /admin — первая доступная вкладка. */
export function AdminIndex() {
  const first = useSections()[0];
  if (first === undefined) {
    return (
      <EmptyState
        icon={ShieldOff}
        title="Нет доступа к администрированию"
        description="Разделы администрирования открываются по правам системной роли."
      />
    );
  }
  return <Navigate to={first.to} replace />;
}
